// Theme resolution, extracted from theme.tsx so it is testable under `node --test`.
// Node 24's type-stripping cannot load .tsx (JSX), so pure logic that needs a unit
// test lives in a plain .ts — the same split as lib/masonry.ts and lib/calendarGrid.ts.

export type Theme = 'dark' | 'light'

export const THEME_KEY = 'rize:theme'

// Written ONLY by the toggle. Its absence is what separates "this user picked a theme"
// from "this browser was handed the default and then persisted it".
export const THEME_CHOSEN_KEY = 'rize:theme:chosen'

// Laura, beta round item 18: "Default the theme to Light — dark is too much."
export const DEFAULT_THEME: Theme = 'light'

/**
 * The initial theme for a browser.
 *
 * Why this is not a one-line default flip: ThemeProvider persists the theme from an
 * effect on EVERY mount, so by now every browser that has ever loaded the app holds
 * 'dark' in localStorage — including Laura's and Dakri's, neither of whom chose it.
 * Treating a stored value as a preference would have pinned every existing user to dark
 * and delivered her request to nobody. So a stored value only counts when
 * THEME_CHOSEN_KEY proves the user actually toggled.
 *
 * The trade-off, stated plainly: someone who DID deliberately switch to dark before
 * this shipped also has no marker, so they get light once and must toggle back — after
 * which it sticks for good. On a three-person beta that is the right side to err on;
 * the alternative silently ignores what the client asked for.
 */
export function resolveInitialTheme(store: Pick<Storage, 'getItem'>): Theme {
  const chose = store.getItem(THEME_CHOSEN_KEY) === 'true'
  const stored = store.getItem(THEME_KEY)
  if (chose && (stored === 'light' || stored === 'dark')) return stored
  return DEFAULT_THEME
}
